// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Renderer.js
//
// Renderers a uTheory.Notation.Music object in a VexFlow context.
// because Vex requires heavily on non camel case, and we want to match
// their property names:
/* eslint-disable camelcase, @typescript-eslint/naming-convention */

import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import type {
  GhostNote,
  SVGContext,
  Formatter,
  Voice as VexVoice,
  StaveTie,
  Tuplet,
} from 'vexflow';
import { Dot } from 'vexflow';
import { StaveNote, BarlineType } from 'vexflow';
import { Renderer as VexRenderer, StaveConnector, Beam, Vex } from 'vexflow';
import { Stave as VexStave } from 'vexflow';
import utils from '../utils';
import TimeSignature from './timeSignature';
import AutoBeam from './autoBeam';
import CustomKeySignature from './customKeySignature';
// eslint-disable-next-line no-unused-vars
import type NoteHolder from './noteHolder';
import { styleStaveNote } from './noteHolder';
import Editable from './editable';
import errorProvider from '../errorProvider';
import type Editor from './editor';
import type Music from './music';
import type Note from './note';
import type { AriaStringOptions } from './note';
import KeySignature from './keySignature';
import type Measure from './measure';
import type Clef from './clef';
import type Staff from './staff';
import config from '../config';
import type UTuplet from './tuplet';
import { isNumber } from '../../../shared/src/utils/isNumber';

type CancelAnimationFunction = () => void;

const uuid = (() => {
  let _uuid = 1;
  return () => _uuid++;
})();

interface Tickable {
  vexObject: StaveNote | GhostNote;
  noteHolder: NoteHolder;
  editable: false | Editable;
  clef: Clef;
}

interface MeasureObject {
  barline: boolean;
  editable?: false | Editable;
  isGhostOrPendingNote: boolean;
  isUncommittedEditable?: boolean;
  left?: number;
  right?: number;
  boxLeft?: number;
  boxRight?: number;
  top: number;
  bottom: number;
  tickable: Tickable | false;
}

export interface RendererOptions {
  backgroundColor: string | false;
  ghostColor: string;
  selectedColor: string;
  staveLineColor: string;
  /**
   * False or integer width. When set, locks layout to this width (in SVG
   * pixels -- does not effect size of SVG in Dom)
   */
  fixedWidth: number | false;
  beaming: {
    /** Show a stemplet above a rest in a beam when true */
    showStemlets: boolean;
    /** Include rests under a beam when true, otherwise break the beam */
    beamRests: boolean;
  };
  /**
   * Number of measures to keep in view when scrolling, or false if not scrolling.
   */
  scrolling: false | number;
  /** Always have a double barline at end, even if not in Music */
  closeLastMeasureWithFinalBarline: boolean;
  /** Always have a single barline at end, even if not in Music */
  closeLastMeasureWithBarline: boolean;
  /** Distance from left of svg that first staff begins */
  x_buffer: number;
  /** Distance from top of svg that first staff begins */
  y_buffer: number;
  /** y spacing after last staff */
  bottom_y_buffer: number;
  /** Distance from clef to first note */
  noteStartBuffer: number;
  /** Max allowed additional x space in measure towards full-justification */
  maxMeasurePadding: number;
  /** Space between each staff */
  staffSpacing: number;
  /** String or function used to define alt text for screen readers on notation elements */
  ariaTitle: false | string | Function | AriaStringOptions;
  ariaLive: false | 'alert' | 'assertive' | 'off' | 'polite' | Function;
}

const defaultOptions: RendererOptions = {
  fixedWidth: false,
  beaming: {
    showStemlets: false,
    beamRests: true,
  },
  scrolling: false,
  closeLastMeasureWithBarline: true,
  closeLastMeasureWithFinalBarline: true,
  x_buffer: 0,
  y_buffer: 0,
  noteStartBuffer: 0,
  maxMeasurePadding: 250,
  staffSpacing: 90,
  bottom_y_buffer: 25,
  staveLineColor: '#777777',
  selectedColor: config.Colors.editorColors.selected,
  ghostColor: config.Colors.editorColors.ghosting,
  backgroundColor: false,
  ariaTitle: false,
  ariaLive: false,
};

const { prefixTransform, prefixTransition } = (() => {
  if (typeof document === 'undefined' || !document.createElement) {
    return {
      prefixTransform: false,
      prefixTransition: false,
    };
  }
  const d = document.createElement('div');
  return {
    prefixTransform: typeof d.style.transform === 'undefined',
    prefixTransition: typeof d.style.transition === 'undefined',
  };
})();

export default class Renderer {
  options: RendererOptions;

  renderer: VexRenderer;

  editor?: Editor;

  ctx: SVGContext;

  /** SVG height (in SVG space -- not DOM pixels) */
  height: number = 100;

  /** SVG width (in SVG space -- not DOM pixels) */
  width: number = 100;

  /** Width of notation in DOM space pixels */
  scrollWidth: number = 0;

  /** Width of each measure in SVG space */
  private measureWidth: number[] = [];

  /** Total measures based on the largest number of measures in any provided voice */
  private totalMeasures: number = 0;

  /** Stave braces, etc... */
  private staveConnectors: StaveConnector[] = [];

  /**
   * VexFlow.Staves[staffNumber][MeasureNumber]
   * Note: this is used by skill-title-component in frontend,
   * and so must be public.
   */
  public staves: VexStave[][] = [];

  /** VexFlow.Formatter */
  private formatter?: Formatter;

  /** voices[measureNumber][voiceNumber] */
  private voices: VexVoice[][] = [];

  /** Note[measureNumber][staffIndex][] */
  private accidentals: Note[][][] = [];

  private customKeySignatures: CustomKeySignature[] = [];

  private ties: StaveTie[] = [];

  private beams: Beam[] = [];

  private vexTuplets: Tuplet[] = [];

  private timeSig: TimeSignature | undefined;

  private keySig: KeySignature = new KeySignature();

  /** Calculated x starting position */
  // tslint:disable-next-line variable-name
  private start_x: number = 0;

  private editableDictionary: {
    staveOrGhostNote: StaveNote | GhostNote;
    editable: Editable;
  }[] = [];

  /* For displaying aria/screen-reader-only content */
  private titleLabel?: HTMLLabelElement;

  /** All notes/ghost notes in form Tickable[measureNumber][voiceNumber][noteNumber] */
  private tickableDictionary: Tickable[][][] = [];

  /**
   * Amount of the SVG visible in scroll width, or the full SVG width if not
   * scrolling (in SVG space -- not DOM pixels)
   */
  visibleWidth: number = 100;

  /** DIVPixels = SVGPixels * this.scale */
  scale: number = 1;

  // True when scrolling and everything fits in this.scrollWidth
  noNeedToScroll: boolean = false;

  editEnabled: boolean = false;

  /**
   * When we're scrolling, this is the outermost div element in the dom; this.div is inside it.
   * When not scrolling, identicle to this.container.
   */
  container: HTMLDivElement;

  /**
   * This is the div in which the SVG is rendered; if not scrolling, it is
   * identicle to this.container.
   */
  div: HTMLDivElement;

  music: Music;

  debug: boolean;

  hooks: {
    type: 'afterRedraw';
    callback: Function;
  }[];

  constructor(music: Music, vexFlowDiv: HTMLDivElement, options: Partial<RendererOptions> = {}) {
    if (typeof window !== 'undefined') (window as any).renderer = this;
    // Some VexFlow display preferences:
    // @todo(vex4): VexFlow 4 doesn't expose this publicly
    // VexStave.LINE_THICKNESS = 1;
    // @todo(vex4): VexFlow 4 doesn't expose this publicly
    // Vex.Flow.STEM_WIDTH = 0.75;
    // @todo(vex4): VexFlow 4 doesn't expose this publicly
    // VexStave.noteYCenteringOffset = -0.289;

    this.div = vexFlowDiv;
    this.div.style.textAlign = 'center';
    this.music = music;
    this.debug = false;

    // as { type: 'afterRedraw', callback }
    this.hooks = [];

    this.options = utils.defaultsDeep(options, defaultOptions);

    if ((options as any).alwaysEndingBarline) {
      options.closeLastMeasureWithBarline = true;
      errorProvider.error(
        'renderer.options.alwaysEndingBarline has been deprecated for options.closeLastMeasureWithBarline',
      );
    }
    if ((options as any).alwaysFinalBarline) {
      options.closeLastMeasureWithFinalBarline = true;
      errorProvider.error(
        'renderer.options.alwaysFinalBarline has been deprecated for options.closeLastMeasureWithFinalBarline',
      );
    }

    this.editEnabled = false; // default -- unless changed.

    const desiredHeight = this.calculateScale();
    this.height = desiredHeight;

    // If we're scrolling, then the canvas becomes the
    // container, and we add an inner div which becomes
    // the new canvas:
    // Otherwise, container and canvas are synonyms!
    //
    // In other words... when scrolling
    //    <this.container><this.div></this.div></this.container>

    this.container = this.div;
    if (this.options.scrolling !== false) {
      this.container.style.overflow = 'scroll';
      const canvas = document.createElement('div');
      this.div = canvas;
      this.container.appendChild(canvas);
    }

    // Set up canvas & context: 2 === SVGContext
    this.renderer = new VexRenderer(this.div, 2);
    this.ctx = this.renderer.getContext() as SVGContext;
    // @todo(vex4): See if scaling works without this in VexFlow 4.0
    // this.ctx.options.scaleToContainer = true;
    this.ctx.setFont('Arial', 10, '');
    // this.ctx.scale(this.options.scale, this.options.scale);
    this.div.style.cursor = 'default';

    this.makeTitleLabel();
  }

  // Public Functions
  log(message: any) {
    if (this.debug) {
      console.log(message);
    }
  }

  private lastSvgElWidth: number = 0;

  private lastSvgElHeight: number = 0;

  setOptions(options: Partial<RendererOptions>) {
    this.options = utils.defaultsDeep(options, this.options);
  }

  /**
   * @param offerredHeight number in pixels to resize to
   */
  responsiveResize(offerredHeight: number) {
    // this.width & this.height refer to the SVG inner height & width dimensions.
    // they do not refer to pixel height of container or canvas!
    // this.log(`Notation resizing to ${offerredHeight}`);

    //      this.log("this.width: " + this.width);
    //      this.log("this.height: " + this.height);

    if (typeof this.width === 'undefined' || typeof this.height === 'undefined') return 0;

    this.height = this.calculateScale();

    this.ctx.svg.style.width = '0px';
    this.ctx.svg.style.height = '0px';

    // If we're scrolling, canvas & container are different
    // but, otherwise they're synonyms.
    // in scrolling:
    // <container><canvas></canvas></container>
    const containerWidth = this.container.clientWidth;
    //        var availableHeight = this.container.clientHeight;
    const availableHeight = offerredHeight;
    /*
      this.log("Notation resizing:");
      this.log("this.width, this.height: " + this.width + ", " + this.height);
      this.log("this.visibleWidth: " + this.visibleWidth);
      this.log("available w, h: " + containerWidth + ", " + availableHeight);
*/
    // Which should we adjust by, width or height?
    const widthMultiplier = containerWidth / this.visibleWidth;
    const heightMultiplier = availableHeight / this.height;

    // See if the widthMultiplier would make it too tall or not:
    // if it would, we assign the heightMultiplier to scale instead.
    const scale =
      widthMultiplier * this.height > offerredHeight ? heightMultiplier : widthMultiplier;

    const width = this.width * scale;
    const height = this.height * scale;
    this.scale = scale;

    // If it all fits in view, we don't need to scroll:
    this.noNeedToScroll = this.width * scale < containerWidth;

    //        this.log("Final width, height: " + width + ", " + height);

    this.ctx.svg.style.height = `${height}px`;
    this.lastSvgElWidth = width;
    this.ctx.svg.style.width = `${width}px`;
    this.lastSvgElHeight = height;
    this.scrollWidth = width;

    this.container.style.height = `${height}px`;

    // Return any left over height:
    return availableHeight - height;
  }

  calculateScale() {
    const staves = this.music.staves.length;
    const desiredHeight =
      staves * this.options.staffSpacing + this.options.y_buffer + this.options.bottom_y_buffer;

    return desiredHeight;
  }

  resetScroll() {
    if (this.options.scrolling !== false) {
      const transition = prefixTransition ? 'webkitTransition' : 'transition';
      const transform = prefixTransform ? 'webkitTransform' : 'transform';
      this.div.style[transition] = 'none';
      this.div.style[transform] = 'translate(0px,0px)';
    }
  }

  setScrollRate(durationSec: number) {
    if (this.options.scrolling !== false) {
      const transform = prefixTransform ? 'webkitTransform' : 'transform';

      // update CSS on this.div
      this.container.style.overflow = 'hidden';
      this.container.style[transform] = 'translate(0px,0px)';
      this.container.scrollLeft = 0;
      if (prefixTransition) {
        this.div.style.webkitTransition = `-webkit-transform ${durationSec}s linear`;
      } else {
        this.div.style.transition = `transform ${durationSec}s linear`;
      }
    }
  }

  disableUserScrolling() {
    const transition = prefixTransition ? 'webkitTransition' : 'transition';
    const transform = prefixTransform ? 'webkitTransform' : 'transform';

    this.div.style[transition] = 'none';
    this.div.style[transform] = 'translateX(0px)';
    this.container.style.overflow = 'hidden';
  }

  /**
   *
   * @param right whether to scroll all the way to the right before enabling
   */
  enableUserScrolling(right: boolean) {
    if (this.options.scrolling !== false) {
      // this.visibleWidth = getVisibleWidthForRhythmScrolling({
      //   measureWidths: this.measureWidth,
      //   music: this.music,
      //   lookaheadSeconds: 3,
      // });

      // console.log({
      //   visibleWidth: this.visibleWidth,
      //   totalWidth: this.measureWidth.reduce(sum, 0),
      // });

      // console.log(
      //   getLineSegmentsForMusic({
      //     measureWidths: this.measureWidth,
      //     music: this.music,
      //     visibleWidth: this.visibleWidth,
      //   }),
      // );
      const transition = prefixTransition ? 'webkitTransition' : 'transition';
      const transform = prefixTransform ? 'webkitTransform' : 'transform';

      const x = right ? (this.width - this.visibleWidth) * this.scale : 0;
      this.div.style[transition] = 'none';
      this.div.style[transform] = 'translateX(0px)';
      this.container.style.overflow = 'scroll';
      this.container.scrollLeft = x;
      this.div.style.willChange = 'transform, webkitTransform';
    }
  }

  scrollTo(measure: number, durationSec?: number) {
    if (typeof durationSec !== 'undefined') {
      this.setScrollRate(durationSec);
    }
    if (this.options.scrolling !== false && !this.noNeedToScroll) {
      const transform = prefixTransform ? 'webkitTransform' : 'transform';

      // Calculate the left offset we need:
      let futureX = this.measureWidth.slice(0, measure).reduce((a, b) => a + b, 0);
      // Scale it to DOM pixels:
      futureX *= this.scale;
      // And make sure it's not beyond the end of the rhythm:
      // GCR: TODO fix maxScroll to be:
      // container.width - this.div.width = max to offset.
      // But let's not scroll beyond the end of the rhythm:
      // calculate when end of rhythm comes into view:
      const maxScroll = this.scrollWidth - this.container.clientWidth;
      futureX = Math.min(futureX, maxScroll);

      // Figure out current x:
      const transformMatrix = window.getComputedStyle(this.div).transform;
      const currentX = transformMatrix.split(',')[4]!.trim();

      // Fix it to current position:
      this.div.style[transform] = `translate(-${currentX}px,0px)`;

      // Start it scrolling!
      this.div.style[transform] = `translate(-${futureX}px,0px)`;
    }
  }

  private getTransformPxForMeasure(measure: number): number {
    // Calculate the left offset we need:
    let futureX = this.measureWidth.slice(0, measure).reduce((a, b) => a + b, 0);
    // Scale it to DOM pixels:
    futureX *= this.scale;
    // And make sure it's not beyond the end of the rhythm:
    // GCR: TODO fix maxScroll to be:
    // container.width - this.div.width = max to offset.
    // But let's not scroll beyond the end of the rhythm:
    // calculate when end of rhythm comes into view:
    const maxScroll = this.scrollWidth - this.container.clientWidth;
    futureX = Math.min(futureX, maxScroll);

    return futureX;
  }

  /**
   * Scrolls presentation of a score accross a given number of seconds, keeping
   * the active moment of playback relatively close to the 0.33 point of the screen width.
   * @param seconds
   */
  animate(seconds: number): () => void {
    this.disableUserScrolling();

    const measureDuration = (1000 * seconds) / this.measureWidth.length;
    // const easingOverlapTime = measureDuration * (1 / 3);

    const animations = this.measureWidth
      .reduce((createdAnimations: (Animation | undefined)[], __, i): (Animation | undefined)[] => {
        if (this.music.isPickup && i === 0) return [...createdAnimations, undefined];
        const endX = this.getTransformPxForMeasure(i);
        const startX = i === 0 ? 0 : this.getTransformPxForMeasure(i - 1);
        const delay = Math.max(measureDuration * i, 0);
        const duration = measureDuration + 0.001;
        const isLast = this.measureWidth.length - 1 === i;
        const animation = this.div.animate(
          [{ transform: `translateX(-${startX}px)` }, { transform: `translateX(-${endX}px)` }],
          {
            delay,
            duration,
            composite: 'replace',
          },
        );

        if (isLast) {
          animation.addEventListener('finish', () => {
            this.div.style.transform = `translateX(-${endX}px)`;
          });
        }
        return [...createdAnimations, animation];
      }, [])
      .filter((truthy) => truthy);

    return () => {
      animations.forEach((animation) => animation?.cancel());
    };
  }

  scrollToEndOfMeasure(measure: number, durationSec: number) {
    //      this.log("Scrolling to measure " + measure);
    if (typeof durationSec !== 'undefined') {
      this.setScrollRate(durationSec);
    }
    if (this.options.scrolling !== false && !this.noNeedToScroll) {
      // Calculate the left offset we need:
      let x = this.width - this.visibleWidth;
      for (let i = measure + 1; i >= 0 && i < this.measureWidth.length; i++) {
        x -= this.measureWidth[i] ?? 0;
      }
      // But let's not scroll beyond the start of the rhythm:
      // calculate when start of rhythm comes into view:
      x = Math.max(x, 0);
      x *= this.scale;

      // Figure out current x:
      const transformMatrix = window.getComputedStyle(this.div).transform;
      const currentX = transformMatrix.split(',')[4]!.trim();

      // Fix it to current position:
      this.div.style.webkitTransform = `translate(-${currentX}px,0px)`;
      this.div.style.transform = `translate(-${currentX}px,0px)`;

      // Start it scrolling!
      this.div.style.webkitTransform = `translate(-${x}px,0px)`;
      this.div.style.transform = `translate(-${x}px,0px)`;
    }
  }

  restoreResponsivenessAfterContextResize() {
    /**
     * Make the SVG responsive
     */
    this.ctx.element.style.width = '';
    this.ctx.svg.style.width = this.lastSvgElWidth ? `${this.lastSvgElWidth}px` : '';
    this.ctx.svg.style.height = this.lastSvgElHeight ? `${this.lastSvgElHeight}px` : '';
    this.ctx.svg.removeAttributeNS(null, 'width');
    this.ctx.svg.removeAttributeNS(null, 'height');
  }

  setCanvasSize() {
    const hasBrace =
      this.staveConnectors &&
      this.staveConnectors.length &&
      // @todo(vex4): When https://github.com/0xfe/vexflow/pull/1343 is merged, replace with .getType()
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.staveConnectors[0]?.['type'] === StaveConnector.type.BRACE;

    const x = hasBrace ? -15 : 0;
    const y = 0;
    const width = Math.ceil(this.width);
    const height = Math.ceil(this.calculateScale());
    this.ctx.resize(width, height);
    this.restoreResponsivenessAfterContextResize();

    // this.log('x', x);
    //      this.div.style.width = width + "px";
    //      this.div.style.height = height + "px";
    if (x !== 0 || y !== 0) {
      // this.log('setting viewBox x: ', x);
      this.ctx.setViewBox(x, y, width, height);
    }
  }

  makeEditable(editor: Editor) {
    this.editor = editor;
    this.editEnabled = true;
  }

  render() {
    this.log('rendering notation');
    // Initialize &/or clear arrays that hold musicObjects:
    this.formatter = new Vex.Flow.Formatter({ softmaxFactor: 2 });
    this.staves = [];
    this.voices = []; // note: these voices are each one measure long
    // unlike their variants in uTheory.Notation.Music.  This is due to
    // the treatment of each measure as a new staff in VexFlow.
    this.measureWidth = [];
    this.customKeySignatures = [];

    this.tickableDictionary = []; // [measureNumber][noteNumber] = staveNote
    this.editableDictionary = [];
    this.ties = [];
    this.beams = [];
    this.vexTuplets = [];

    // load in key & time signatures:
    this.timeSig = this.music.getTimeSignature(0); // may be null
    this.keySig = this.music.keySignatures[0];

    // tally measures
    this.totalMeasures = this.getMeasureCount();

    // Build holders for all stavenotes, etc...
    // creates this.tickableDictionary[measureNumber] = [];
    this.initializeArrays();

    // Create vexFlow staves (one stave per measure)
    this.initializeStaves();

    // Add music objects to voices, then determine needed staff size
    this.prepareMeasures();

    // Adjust the staff width according to the above & format it all
    this.prepareStaves();

    // And set the canvas size to fit it all:
    this.setCanvasSize();

    // Draw it all
    this.redraw();
    this.updateTitleLabel();

    // Set visible width for scrolling:
    // if (this.options.scrolling) {
    //   this.visibleWidth = getVisibleWidthForRhythmScrolling({
    //     measureWidths: this.measureWidth,
    //     music: this.music,
    //     lookaheadSeconds: 3,
    //   });
    // }
  }

  initializeArrays() {
    for (let i = 0; i < this.totalMeasures; i++) {
      this.tickableDictionary[i] = [];
    }
  }

  initializeStaves() {
    // Setup this.staves[staff][measureStaff]
    this.staves = [];
    for (let staffCount = 0; staffCount < this.music.staves.length; staffCount++) {
      this.staves[staffCount] = [];
      for (let measureCount = 0; measureCount < this.totalMeasures; measureCount++) {
        const vexStave = new VexStave(0, 0, 100, {
          fill_style: this.options.staveLineColor,
        });
        const currentStave = this.staves[staffCount];
        if (!currentStave) {
          throw Error('Can not set vexStave for measure without existing staff');
        }
        currentStave[measureCount] = vexStave;
        vexStave.setAttribute('id', `vex-stave-${uuid()}`);
      }
    }
  }

  prepareMeasures() {
    this.accidentals = []; // accidentals[measure][staff][list]

    // Build each measure:
    for (let measureNumber = 0; measureNumber < this.totalMeasures; measureNumber++) {
      // initialize the voice-holder array for the measure:
      this.voices[measureNumber] = [];
      this.accidentals[measureNumber] = [];

      // for each voices, add notes to the voice holder:
      const fixedWidth = this.prepareMeasureVoices(measureNumber);

      // Format the voices together -- or not???:
      const formatter = new Vex.Flow.Formatter();
      // this.log(formatter);
      formatter.preCalculateMinTotalWidth(this.voices[measureNumber]!);

      const width =
        fixedWidth === false
          ? formatter.getMinTotalWidth() + 5
          : // + this.voices[measureNumber][0].stave.getNoteStartX()
            fixedWidth;

      this.log(`Measure ${measureNumber} width: ${width} fixedWidth: ${fixedWidth}`);

      // In the event that the measure is fixed-width,
      // lock it down to its fixed width:
      //     if(fixedWidth !== false) width = fixedWidth;

      this.log(`this.measureWidth[${measureNumber}] = ${width}`);
      this.measureWidth[measureNumber] = width;
    }
  }

  prepareMeasureVoices(measureNumber: number): number | false {
    let fixedWidth: number | false = false;
    const measureVoices = this.voices[measureNumber];
    const measureAccidentals = this.accidentals[measureNumber];

    if (!measureVoices || !measureAccidentals) {
      throw Error(`MeasureVoices and MeasureAccidentals must be defined to prepare MeasureVoices`);
    }

    const formatter = this.formatter;
    if (!formatter) {
      throw Error(`Renderer.Formatter must be initialized before preparing measure voices.`);
    }

    // Work through each uTheory voice -- just process one measure.
    this.music.voices.forEach((voice, voiceNumber) => {
      const currentStaff = voice.staff;
      if (!currentStaff) {
        throw Error(
          `Cannot prepare measure voices for m. ${measureNumber} when no staff exists for measure`,
        );
      }
      const staffIndex = this.music.staffToIndex(currentStaff);
      const otherVoiceOnStaff = this.music.voices.filter(
        (musicVoice) => musicVoice.staff === currentStaff && musicVoice !== voice,
      )[0];
      let stemDirection: -1 | 0 | 1 = 0;
      if (otherVoiceOnStaff) {
        stemDirection =
          this.music.voices.indexOf(voice) < this.music.voices.indexOf(otherVoiceOnStaff) ? 1 : -1;
      }

      measureAccidentals[staffIndex] = [];

      // pull one measure's notes from this.music
      const measure = voice.getMeasure(measureNumber);

      // note if it's fixed width:
      if (measure && measure.style.fixedWidth !== false) {
        fixedWidth = Math.max(measure.style.fixedWidth, Number(fixedWidth));
      }

      const vexVoice = this.prepareSingleMeasure(
        measure,
        measureNumber,
        voiceNumber,
        stemDirection,
      );

      // Add the voice to an array for this measure:
      // which will be used for later drawing.
      if (vexVoice) measureVoices[voiceNumber] = vexVoice;
    });

    // Join any voices that share a staff into one, so that their notes &
    // accidentals won't crash. (Also lays out accidentals for first time,
    // so even if a voice doesn't share a staff, we still join it with itself).
    utils.groupBy(measureVoices, 'stave').forEach((voices) => {
      formatter.joinVoices(voices);
    });

    return fixedWidth;
  }

  prepareSingleMeasure(
    measure: Measure | false,
    measureNumber: number,
    voiceNumber: number,
    stemDirection: -1 | 0 | 1 = 0,
  ) {
    if (measure === false) return false; // it's empty, leave it alone.

    const musicVoice = this.music.voices[voiceNumber];
    if (!musicVoice) {
      throw Error(`Cannot prepare measure when voices[${voiceNumber}] is undefined`);
    }

    const staff = musicVoice.staff;
    if (!staff) {
      throw Error(
        `Cannot prepare measure when voice ${voiceNumber} has not been assigned a staff.`,
      );
    }

    const measureTickables = this.tickableDictionary[measureNumber];
    if (!measureTickables) {
      throw Error(
        `Cannot prepare measure ${measureNumber} when tickableDictionary has not been created.`,
      );
    }

    const clef = staff.getClef();
    const staffIndex = this.music.staffToIndex(staff);
    const vexStave = this.staves[staffIndex]?.[measureNumber];

    if (!vexStave) {
      throw Error(
        `Cannot prepare measure when a vexStave hasn't been created for staff index ${staffIndex}`,
      );
    }

    const timeSig = typeof this.timeSig === 'undefined' ? new TimeSignature('4/4') : this.timeSig;
    let upper = timeSig.upper;
    if (measure.isPickup) {
      if (timeSig.isSimple) {
        upper = Math.ceil(measure.getTotalBeats().valueOf());
      } else {
        upper = Math.ceil(measure.getTotalBeats().valueOf() * 3);
      }
    }
    const time = {
      num_beats: upper,
      beat_value: timeSig.lower,
      resolution: Vex.Flow.RESOLUTION,
    };

    // Apply barline formatting:
    // this.styleMeasure(measure, vexStave);

    // If the measure is not complete, add ghost note(s) to fill out the measure.
    // failing to do so results in formatting nightmares if one voice is complete and another isn't.
    // ...but: Don't do this if it's a pickup measure!
    // if (!measure.isPickup) {
    this.log('completing measure');
    this.log(measure);
    measure.getMeasureCompletion(`${upper}/${timeSig.lower}` as TimeSignatureAlias, {
      ghost: true,
      destination: measure, // fill it onto itself.
    });
    // }

    // Format each note/chord:
    const noteHolders = measure.getNoteHolders();
    const autoBeam = noteHolders.every((nh) => !!nh.rhythm && nh.rhythm.beaming === 0);

    const tupletArray = noteHolders
      .map((nh) => nh.rhythm?.tuplets)
      .flat()
      .filter((t): t is UTuplet => !!t);

    const measureTuplets = new Set(tupletArray);
    const tickables = this.getMeasureVoiceTickableDictionaryEntries(
      noteHolders,
      clef,
      measureNumber,
      staffIndex,
      voiceNumber,
      stemDirection,
    );
    measureTickables[voiceNumber] = tickables;
    const vexObjects = tickables.map((t) => t.vexObject);

    // autoBeam!
    if (autoBeam) {
      this.autoBeamMeasureVoice(measure, timeSig, stemDirection);
    }

    measureTuplets.forEach((tuplet) => {
      // Ghostnotes are not tupletable... GCR TODO: extend functionality to
      // VexFlow for drawing tuplets in real time.
      const tupletStaveNotes = tuplet.noteHolders.map((nh) => nh.staveNote).filter(isStaveNote);
      const vexTuplet = new Vex.Flow.Tuplet(tupletStaveNotes, {
        num_notes: tuplet.numNotes,
        notes_occupied: tuplet.notesOccupied,
      });
      this.vexTuplets.push(vexTuplet);
    });

    // Create a new voice & set parameters:
    const voice = new Vex.Flow.Voice(time);
    voice.setMode(Vex.Flow.Voice.Mode.SOFT);
    voice.setStave(vexStave);

    // Add them to a voice:
    voice.addTickables(vexObjects);

    return voice;
  }

  autoBeamMeasureVoice(measure: Measure, timeSig: TimeSignature, stemDirection: 0 | 1 | -1) {
    const groups = AutoBeam(measure, timeSig, this.options.beaming.beamRests);
    groups.forEach((beamGroup) => {
      // eslint-disable-next-line no-shadow
      const staveNotes = beamGroup.map((beamNote) => beamNote.holder.staveNote!);
      const autoStem = stemDirection === 0;
      const beam = new Beam(staveNotes, autoStem);
      // If it's a beamable rest at the 16th note level or smaller, we need a stemlet because
      // of a vexflow bug that will always render the secondary beam when a rest is beamed,
      // whether or not there's a stemlet.
      let forceStemlet = false;
      beamGroup.forEach((beamNote, i) => {
        forceStemlet =
          forceStemlet ||
          (!!beamNote.isBeamable &&
            beamNote.holder.isRest() &&
            beamNote.holder.rhythm!.fraction.lessThan(1 / 8));
        if (beamNote.partialDirection && beamNote.isBeamable) {
          beam.setPartialBeamSideAt(i, beamNote.partialDirection === 'left' ? 'L' : 'R');
        }
      });
      beam.render_options.show_stemlets = this.options.beaming.showStemlets || forceStemlet;
      beam.setContext(this.ctx);
      this.beams.push(beam);
    });
  }

  getMeasureVoiceTickableDictionaryEntries(
    noteHolders: NoteHolder[],
    clef: Clef,
    measureNumber: number,
    staffIndex: number,
    voiceIndex: number,
    stemDirection: -1 | 0 | 1 = 0,
  ) {
    let priorVexObject: StaveNote | GhostNote;
    return noteHolders.map((noteHolder): Tickable => {
      // Create a staveNote for each NoteHolder:
      const staveNote = this.noteHolderToVexObject(
        noteHolder,
        clef,
        measureNumber,
        staffIndex,
        noteHolders,
        stemDirection,
      );

      // If there's an annotation (text needing to be rendered),
      // attach it to the staveNote:
      if (noteHolder.annotation) {
        noteHolder.annotation.setContext(this.ctx);

        // if (vexObject instanceof Vex.Flow.StaveNote) {
        staveNote.addModifier(noteHolder.annotation);
        // } else {
        // Ghost notes do things differently...
        // vexObject.addModifier(noteHolder.annotation, 0);
        // }
      }

      // Attach the staveNote to the noteHolder -- this will be used
      // for add-on uTheory.Vex objects to draw themselves:
      // (though it is not used within this renderer.js)
      noteHolder.staveNote = staveNote;

      if (noteHolder.rhythm?.isTied) {
        let priorNote: StaveNote | GhostNote | undefined;
        if (priorVexObject) {
          priorNote = priorVexObject;
        } else {
          const lastMeasureNotes = this.tickableDictionary[measureNumber - 1]?.[voiceIndex];
          priorNote = lastMeasureNotes?.[lastMeasureNotes.length - 1]?.vexObject;
        }
        if (priorNote instanceof Vex.Flow.StaveNote && staveNote instanceof Vex.Flow.StaveNote) {
          this.createTie(priorNote, staveNote, stemDirection);
        }
      }
      priorVexObject = staveNote;
      return {
        vexObject: staveNote,
        noteHolder,
        editable: noteHolder.editable || false,
        clef,
      };
    });
  }

  styleMeasure(measureCount: number, staffCount: number, vexStave: VexStave) {
    // from vexflow stavebarline.js
    const barlineTypes = {
      SINGLE: 1,
      DOUBLE: 2,
      END: 3,
      REPEAT_BEGIN: 4,
      REPEAT_END: 5,
      REPEAT_BOTH: 6,
      NONE: 7,
    };

    this.music.voices.forEach((voice) => {
      const measure = voice.getMeasure(measureCount);
      if (measure === false) return;
      const staff = voice.staff;
      // This should never happen
      if (!staff) return;

      const staffIndex = this.music.staffToIndex(staff);
      if (staffCount !== staffIndex) return;

      let type;
      if (typeof measure.style.beginningBarline !== 'undefined') {
        type = measure.style.beginningBarline.toUpperCase();
        const barlineStyle = barlineTypes[type as unknown as keyof typeof barlineTypes];
        if (barlineStyle) {
          vexStave.setBegBarType(barlineStyle);
        }
      } else if (measureCount === 0) {
        // If it's the first measure, then by default, we don't draw an opening barline:
        vexStave.setBegBarType(Vex.Flow.Barline.type.NONE);
      }
      if (typeof measure.style.endingBarline !== 'undefined') {
        type = measure.style.endingBarline.toUpperCase();
        const barlineStyle = barlineTypes[type as unknown as keyof typeof barlineTypes];
        if (barlineStyle) {
          vexStave.setEndBarType(barlineStyle);
        }
      }
    });
  }

  createTie(firstNote: StaveNote, secondNote: StaveNote, tieDirection: -1 | 0 | 1) {
    // be sure the notes have the same number of keys -- otherwise no dice!
    // fail gracefully and don't create the tie.
    if (firstNote.keys.length !== secondNote.keys.length) return;
    const indices = [];
    for (let i = 0; i < firstNote.keys.length; i++) indices[i] = i;

    const tie = new Vex.Flow.StaveTie({
      first_note: firstNote,
      last_note: secondNote,
      first_indices: indices,
      last_indices: indices,
    });
    if (tieDirection) {
      tie.setDirection(-tieDirection as 1 | -1);
    }
    tie.setContext(this.ctx);

    this.ties.push(tie);
  }

  getMeasureCount() {
    return this.music.getTotalMeasureNumbers();
  }

  prepareStaves() {
    // tslint:disable-next-line variable-name
    let start_x = 0;
    // Setup the first measure of each staff
    this.music.staves.forEach((musicStaff, i) => {
      let clef = musicStaff.getClef();
      const vexStave = this.staves[i]?.[0];
      if (!vexStave) {
        throw Error(`Cannot prepare stave when renderer.staves[${i}] is undefined`);
      }
      vexStave.setBegBarType(BarlineType.NONE);
      vexStave.setX(this.options.x_buffer);
      vexStave.setY(this.options.y_buffer + this.options.staffSpacing * i);
      vexStave.setWidth(this.measureWidth[0] ?? 100);
      this.styleMeasure(0, i, vexStave);

      if (!clef.hidden) {
        vexStave.addClef(clef.getVexName());
      }

      // Add keySignature:
      this.addKeySignature(vexStave, musicStaff);

      // // If there's a custom key sig, add it:
      // if (
      //   musicStaves[i].hasOwnProperty('customKeySignature') &&
      //   musicStaves[i].customKeySignature instanceof CustomKeySignature
      // ) {
      //   const { customKeySignature } = musicStaves[i];
      //   customKeySignature.setClef(musicStaves[i].getClef());
      //   this.staves[i][0].addModifier(customKeySignature);
      //   this.customKeySignatures.push(customKeySignature);
      // }

      // Add first time signature:
      const visibleTimeSignatureExists =
        typeof this.timeSig !== 'undefined' && !this.timeSig.hidden;
      const hasMultipleStaves = this.music.staves.length > 1;
      let defaultTimeSignaturePadding = 10; // VexFlow see timesignature.js:15
      if (hasMultipleStaves && visibleTimeSignatureExists) {
        let clefSpecificTimeSigPadding = 0;
        if (clef.clefName === 'treble') {
          clefSpecificTimeSigPadding = 2;
        } else if (clef.clefName === 'percussion') {
          clefSpecificTimeSigPadding = 13;
        } else if (clef.clefName === 'bass' || clef.clefName === 'baritone') {
          clefSpecificTimeSigPadding = 0;
        } else {
          // C-clefs
          clefSpecificTimeSigPadding = 1;
        }
        defaultTimeSignaturePadding += clefSpecificTimeSigPadding;
      }
      if (visibleTimeSignatureExists) {
        vexStave.addTimeSignature(this.timeSig!.getVexName(), defaultTimeSignaturePadding);
      }

      // Compare for the largest start_x and make them match.
      // (In case one clef is wider than another...)
      start_x = Math.max(start_x, vexStave.getNoteStartX());

      // Prepare to draw it:
      vexStave.setContext(this.ctx);
    });

    this.start_x = start_x;

    // 5 for a bit of padding after key sig.
    this.measureWidth[0] += this.start_x;
    this.log(`measureWidth[0] = ${this.measureWidth[0]} startX: ${start_x}`);
    // Now that we know spacing of time sig, etc... let's calculate measure buffers:
    // calculate whitespace & adjust spacing accordingly
    this.justifyMeasureSpacing();

    // Now resize the first measures:
    this.staves.forEach((measureStaves, i) => {
      measureStaves[0]?.setWidth(this.measureWidth[0] ?? 100);
    });

    // Create & size the other measure staves:
    for (let measureCount = 1; measureCount < this.totalMeasures; measureCount++) {
      for (let staffCount = 0; staffCount < this.music.staves.length; staffCount++) {
        const previousStaff = this.staves[staffCount]?.[measureCount - 1];
        if (!previousStaff) {
          throw Error(
            `Cannot format prepare stave ${staffCount}, m. ${measureCount} when prior stave hasn't been prepared.`,
          );
        }
        // @todo(vex4): we might actually want to use previousStaff.x + previousStaff.width?
        start_x = previousStaff.getNoteEndX();
        // tslint:disable-next-line variable-name
        const start_y = previousStaff.getY();
        const width = this.measureWidth[measureCount] ?? 100;
        const staffClef = this.music.staves[staffCount]?.getClef();
        if (!staffClef) {
          throw Error(`staff must have a cleff to format.`);
        }

        const vexStaveOptions = {
          fill_style: this.options.staveLineColor,
          clef: staffClef.getVexName(),
          right_bar: this.options.closeLastMeasureWithBarline,
        };
        const vexStave = new VexStave(start_x, start_y, width, vexStaveOptions);
        const currentStaffVexStaves = this.staves[staffCount];
        if (!currentStaffVexStaves) {
          throw Error(
            `Cannot add vexStave to current staff vexStaves array when it hasn't been initialized.`,
          );
        }
        currentStaffVexStaves[measureCount] = vexStave;
        vexStave.setAttribute('id', `vex-stave-${uuid}`);
        this.styleMeasure(measureCount, staffCount, vexStave);
      }
    }

    this.staveConnectors = [];
    //      if(this.staves.length>1) {
    if (this.music.staves.length > 1) {
      // this.log('setting brace');
      this.width += 15;
      const firstStave = this.staves[0]?.[0];
      const lastStave = this.staves[this.staves.length - 1]?.[0];
      if (firstStave && lastStave) {
        this.staveConnectors.push(
          new StaveConnector(firstStave, lastStave)
            .setType(StaveConnector.type.BRACE)
            .setContext(this.ctx),
        );
        this.staveConnectors.push(
          new StaveConnector(firstStave, lastStave)
            .setType(StaveConnector.type.SINGLE_LEFT)
            .setContext(this.ctx),
        );
      }
    }

    if (this.options.closeLastMeasureWithFinalBarline) {
      const firstStaveLastMeasure = this.staves[0]?.[this.totalMeasures - 1];
      const lastStaveLastMeasure = this.staves[this.staves.length - 1]?.[this.totalMeasures - 1];
      if (firstStaveLastMeasure && lastStaveLastMeasure) {
        this.staveConnectors.push(
          new StaveConnector(firstStaveLastMeasure, lastStaveLastMeasure)
            .setType(StaveConnector.type.BOLD_DOUBLE_RIGHT)
            .setContext(this.ctx),
        );
      }
    }

    this.setVoiceStaves();

    const firstStaveFirstMeasure = this.staves[0]?.[0];
    if (!firstStaveFirstMeasure) {
      throw Error(`Cannot return x of prepared staves when first stave wasn't prepared.`);
    }

    return firstStaveFirstMeasure.getX();
  }

  private setVoiceStaves() {
    this.voices.forEach((measureVoices, measureNumber) => {
      measureVoices.forEach((voice, index) => {
        const staffIndex = this.music.voiceIndexToStaffIndex(index);
        const measureStaveHolders = this.staves[staffIndex];
        if (!measureStaveHolders) {
          throw Error(
            `Cannot set voice staves when this.staves[${staffIndex}] has not been initialized.`,
          );
        }
        const stave = measureStaveHolders[measureNumber];
        if (!stave) {
          throw Error(
            `Cannot set voice stave when renderer.staves[${staffIndex}][${measureNumber}] has not been initialized.`,
          );
        }
        voice.setStave(stave);
      });
    });
  }

  addKeySignature(vexStaff: VexStave, musicStaff: Staff) {
    // If there is a custom key signature on this staff, it overrides
    // any manual one:
    if (
      musicStaff.customKeySignature &&
      musicStaff.customKeySignature instanceof CustomKeySignature
    ) {
      musicStaff.customKeySignature.setClef(musicStaff.getClef());
      vexStaff.addModifier(musicStaff.customKeySignature);
      this.customKeySignatures.push(musicStaff.customKeySignature);
      return;
    }

    const vexKeyName = this.keySig.toVexString();
    const vexKeySig = new Vex.Flow.KeySignature(vexKeyName);

    vexStaff.addModifier(vexKeySig);
  }

  justifyMeasureSpacing() {
    const formatter = this.formatter;
    if (!formatter) {
      throw Error(
        `Cannot justify measure spacing when renderer.formatter has not been initialized.`,
      );
    }
    const numberOfMeasures = this.measureWidth.length;
    const minRequiredWidth = this.measureWidth.reduce((a, b) => a + b, 0);
    const maxMeasurePadding = this.options.maxMeasurePadding || 0; // max padding per bar in pixels

    // Determine available width, or just use what we've got:
    const availableWidth = this.options.scrolling
      ? minRequiredWidth * 2
      : this.options.fixedWidth
      ? this.options.fixedWidth
      : minRequiredWidth * 1.5; // formerly 2... too wide I think.

    this.log({
      availableWidth,
      minRequiredWidth,
      measureWidth: [...this.measureWidth],
      xBuffer: this.options.x_buffer,
    });

    const whiteSpace = availableWidth - minRequiredWidth - 2 * this.options.x_buffer;

    if (whiteSpace < 0) this.log('Out of horizontal space!!!'); // this should only happen in fixedWidth situation

    const measurePadding = Math.min(whiteSpace / numberOfMeasures, maxMeasurePadding);

    const totalPadding = measurePadding * numberOfMeasures;

    // Format each measure-voice:

    // The padding is distributed so that longer measures
    // get more padding -- call this proportionalPadding.
    const proportionalPadding = 0.7 * totalPadding;
    // Remove the first measure clef, etc.. from the proportion:

    // And every measure gets an even portion of the left
    // over padding: call it nonProportionalPadding.
    const nonProportionalPadding = (1 - 0.7) * totalPadding;

    // Log it out:
    /*
    this.log(`minRequiredWidth: ${minRequiredWidth}
      maxMeasurePadding: ${maxMeasurePadding}
      availableWidth: ${availableWidth}
      whiteSpace: ${whiteSpace}
      measurePadding: ${measurePadding}
      totalPadding: ${totalPadding}
      proportionalPadding: ${proportionalPadding}
      nonProportionalPadding: ${nonProportionalPadding}`);

    this.log(`this.measureWidth: ${this.measureWidth.join(', ')}`);
*/
    // this.voices[measureNumber][voiceNumber]
    // add padding evenly to each measure:
    this.log({ measureWidthBeforePadding: [...this.measureWidth] });
    this.measureWidth = this.measureWidth.map((width, measureNumber) => {
      const clefWidth = measureNumber ? 0 : this.start_x; // see prepareStaves()
      const pct = (width - clefWidth) / (minRequiredWidth - this.start_x);
      this.log(`pct: ${pct}`);
      width += pct * proportionalPadding + nonProportionalPadding / numberOfMeasures;

      // GCR: Kluge. We add 17px to each measure because VexFlow doesn't
      // count this in it's width calculation. These come from:
      // - note.render_options.stave_padding = 12 (there's always 12px from
      // stave.start_x to the first staveNote)
      // - stave.start_x = x + 5; // fixed in the code
      // TODO: Make stave_padding configurable; make the + 5 configurable
      // within the VexFlow code.
      const isLastMeasure = measureNumber === numberOfMeasures - 1;
      const thisMeasurePadding = isLastMeasure ? 25 : 17;
      // const thisMeasurePadding = 0;

      const measureVoices = this.voices[measureNumber];
      // const formatOptions = {
      //   align_rests: true,
      //   context: this.ctx,
      // };

      if (!measureVoices) {
        throw Error(
          `Cannot justify measure spacing when renderer.voices[${measureNumber}] has not been initialized.`,
        );
      }
      this.staves[measureNumber]?.forEach((stave) => stave.setWidth(width));
      formatter.format(measureVoices, width - clefWidth - thisMeasurePadding); // GCR TODO Kluge

      // vexflow has a bug that causes stave.getNoteEndX() to be calculated
      // incorrectly. This subtraction of 8 pixels seems to prevent the last note
      // in a bar from running over the barline.

      return width;
    });
    this.log({ measureWidthAfterPadding: [...this.measureWidth] });

    this.log(`this.measureWidth: ${this.measureWidth.join(', ')}`);

    // If scrolling, determine visible width for scale calc:
    // Determine the visible width -- usually the same as
    // availableWidth, except in the case of a scrolling
    // situation:

    let visibleWidth = availableWidth;

    // If we're scrolling, we size for the number of measures to
    // keep in view, indicated in this.options.scrolling.
    // We do a double loop here, to find the largest two measures,
    // to be sure that what someone is performing never falls out
    // of the viewframe:
    if (this.options.scrolling !== false) {
      visibleWidth = 0;
      const scrollMeasures = this.options.scrolling;
      for (let i = scrollMeasures - 1; i < numberOfMeasures; i++) {
        let subMeasuresWidth = 0;
        for (let j = i; j > i - scrollMeasures; j--) {
          subMeasuresWidth += this.measureWidth[j] ?? 100;
        }
        visibleWidth = Math.max(visibleWidth, subMeasuresWidth);
      }
    }
    this.visibleWidth = visibleWidth;

    // If we have a left brace, we need 15px for it:
    this.width = this.measureWidth.reduce((a, b) => a + b);
  }

  noteHolderToVexObject(
    noteHolder: NoteHolder,
    clef: Clef,
    measureNumber: number,
    staffIndex: number,
    noteHolders: NoteHolder[],
    stemDirection: -1 | 0 | 1 = 0,
  ) {
    const rhythm = noteHolder.rhythm;
    if (!rhythm) {
      throw Error(`Cannot convert noteholder without rhythm to vex object.`);
    }
    const customKeySignature = this.music.staves[staffIndex]?.customKeySignature;
    const keySig = customKeySignature || this.keySig;
    const baseDuration = rhythm.baseDuration === 'b' ? '1/2' : rhythm.baseDuration;
    let dots = '';
    for (let dot = 0; dot < rhythm.getDots(); dot++) dots += 'd';

    // var align_center = (baseDuration == "w");
    // GCR TO-DO: Kluge... align_center should work, but whole rests look
    // awful... figure out why & fix
    const { rendersAs } = noteHolder;

    if (rendersAs === 'note') {
      const measureAccidentals = this.accidentals[measureNumber]?.[staffIndex];
      if (!measureAccidentals) {
        throw Error(
          `Cannot set accidental when renderer.accidentals[measureNumber: ${measureNumber}][staffIndex: ${staffIndex}] has not been initialized`,
        );
      }
      // push something to stave
      const staveNote = noteHolder.getStaveNote(
        clef,
        measureAccidentals,
        keySig,
        this.options,
        stemDirection,
      );

      const vexStave = this.staves[staffIndex]?.[measureNumber];
      if (!vexStave) {
        throw Error(
          `Cannot set the staveNote VexStave when renderer.staves[staffIndex: ${staffIndex}][measureNumber: ${measureNumber}] has not been initialized`,
        );
      }
      staveNote.setStave(vexStave);

      if (noteHolder.editable) {
        const editable = noteHolder.editable;
        // add to the editableDictionary so we can get bounding boxes for it later.
        this.editableDictionary.push({
          staveOrGhostNote: staveNote,
          editable,
        });
        // set it's renderer to this:
        editable.setRenderer(this);
        // if (!this.editor) {
        //   throw Error(`Cannot set editable editor when renderer.editor has not been initialized.`);
        // }
        if (this.editor) {
          editable.setEditor(this.editor);
        }
      }
      return staveNote;
    }

    if (rendersAs === 'rest' || rendersAs === 'pendingRest' || rendersAs === 'placeHolderRest') {
      const pending = rendersAs !== 'rest';
      return this.noteHolderToRest(
        noteHolder,
        clef,
        baseDuration,
        dots,
        pending,
        noteHolders.length > 1,
      );
    }

    if (rendersAs === 'ghostNote') {
      // So that ghost notes actually take up a reasonable amount of space,
      // which is what we want them to do (instead of vex's 0-width ones),
      // we actually render them as invisible rests of the specified duration.
      // This has the advantage of breaking beams, etc...

      // const ghostNote = new Vex.Flow.GhostNote({ duration: baseDuration + dots });
      if (noteHolder.editable) {
        // set it's renderer to this:
        noteHolder.editable.setRenderer(this);
        if (!this.editor) {
          throw Error(`Cannot set editable editor when renderer.editor has not been initialized.`);
        }
        noteHolder.editable.setEditor(this.editor);
      }
      return this.noteHolderToRest(noteHolder, clef, baseDuration, dots, true, true, true);
    }

    throw new Error(`Invalid noteHolder.rendersAs response: ${rendersAs}`);
  }

  noteHolderToRest(
    noteHolder: NoteHolder,
    clef: Clef,
    baseDuration: string,
    dots: string,
    pending = false,
    multipleNotesInBar = false,
    ghosting = false,
  ) {
    // tslint:disable-next-line variable-name
    const align_center = baseDuration === 'w' && !noteHolder.doNotCenter && !multipleNotesInBar;

    const staveNote = new StaveNote({
      keys: ['r/4'],
      clef: clef.getVexName(),
      duration: `${baseDuration + dots}r`,
      align_center,
    });

    // if (align_center) {
    //   this.centeredStaveNotes.push(staveNote);
    // }
    if (pending) {
      staveNote.setStyle({
        fillStyle: this.options.ghostColor,
        strokeStyle: this.options.ghostColor,
      });
    }

    if (ghosting) {
      staveNote.setStyle({
        fillStyle: `${this.options.ghostColor}00`,
        strokeStyle: `${this.options.ghostColor}00`,
      });
    }

    if (noteHolder.editable && noteHolder.editable.selected) {
      staveNote.setStyle({
        strokeStyle: this.options.selectedColor,
        fillStyle: this.options.selectedColor,
      });
    }

    // @todo(vex4): Be sure dots are still showing up -- we set them in the staveNote constructor above
    for (let i = 0; i < (noteHolder.rhythm?.dots || 0); i++)
      Dot.buildAndAttach([staveNote], { all: true });
    // for (let i = 0; i < noteHolder.rhythm.dots; i++) staveNote.addDotToAll();
    styleStaveNote(staveNote, noteHolder, [], {
      selectedColor: this.options.selectedColor,
      ghostColor: this.options.ghostColor,
    });
    return staveNote;
  }

  makeTitleLabel() {
    const titleLabel = document.createElement('label');
    this.ctx.svg.id = `notation-element-${uuid()}`;
    titleLabel.setAttribute('for', this.ctx.svg.id);
    titleLabel.classList.add('sr-only');
    titleLabel.id = `notation-element-${uuid()}-label`;
    this.div.appendChild(titleLabel);
    this.titleLabel = titleLabel;
    return titleLabel;
  }

  updateTitleLabel() {
    if (!this.titleLabel) {
      this.titleLabel = this.makeTitleLabel();
    }
    const titleEl = this.titleLabel;
    const ariaOptions =
      utils.isObject(this.options.ariaTitle) && !utils.isFunction(this.options.ariaTitle)
        ? this.options.ariaTitle
        : {};
    let ariaTitle = this.music.toAriaString(ariaOptions);
    if (this.options.ariaTitle) {
      if (typeof this.options.ariaTitle === 'string') {
        ariaTitle = this.options.ariaTitle;
      }
      if (typeof this.options.ariaTitle === 'function') {
        ariaTitle = this.options.ariaTitle();
      }
    }

    let ariaLive = 'polite';
    if (this.options.ariaLive) {
      ariaLive =
        typeof this.options.ariaLive === 'string' ? this.options.ariaLive : this.options.ariaLive();
    }
    // this.log('setting aria-live', ariaLive);
    if (ariaLive === 'alert') {
      titleEl.removeAttribute('aria-live');
      titleEl.setAttribute('role', 'alert');
    } else {
      titleEl.setAttribute('aria-live', ariaLive);
      titleEl.removeAttribute('role');
    }

    // this.log('setting title', ariaTitle);
    titleEl.innerHTML = ariaTitle;
  }

  redraw() {
    if (!this.titleLabel) {
      this.titleLabel = this.makeTitleLabel();
    }
    this.ctx.clear();
    this.setCanvasSize();
    this.ctx.svg.tabIndex = 1;
    this.ctx.svg.setAttribute('aria-labelledby', this.titleLabel.id);
    if (this.options.backgroundColor) {
      this.ctx
        .save()
        .setStrokeStyle(this.options.backgroundColor)
        .setFillStyle(this.options.backgroundColor)
        .fillRect(-25, 0, this.width + 25, this.height)
        .restore();
    }

    // Align the notes of the first measure, due to clef size difference:
    this.staves.map((staves) => staves[0]).forEach((stave) => stave?.setNoteStartX(this.start_x));

    this.staves.flat(2).forEach((stave) => {
      stave.setStyle({ fillStyle: this.options.staveLineColor });
      stave.setContext(this.ctx).draw();
    });

    this.voices.flat(2).forEach((voice) => {
      voice.draw(this.ctx, voice.getStave());
    });

    // this.customKeySignatures.forEach(cks => {
    //   cks.format(this.ctx);
    //   cks.draw();
    // });

    if (this.ties.length > 0)
      this.ties.forEach((tie) => {
        tie.draw();
      });

    if (this.beams.length > 0) {
      this.beams.forEach((beam) => {
        beam.postFormatted = false;
        beam.draw();
      });
    }

    // Draw any tuplets:
    this.vexTuplets.forEach((vexTuplet) => vexTuplet.setContext(this.ctx).draw());

    if (typeof this.staveConnectors !== 'undefined') {
      this.staveConnectors.forEach((connector) => {
        connector.draw();
      });
    }

    if (this.editEnabled) this.setBoundingBoxes();

    if (this.music.rhythmChecks && this.music.rhythmChecks.length) {
      this.music.rhythmChecks.forEach((rc) => {
        rc.draw();
      });
    }

    this.afterRedraw();
  }

  addHook(type: (typeof this.hooks)[number]['type'], callback: () => void) {
    this.hooks.push({
      type,
      callback,
    });
  }

  afterRedraw() {
    this.hooks.filter((l) => l.type === 'afterRedraw').forEach((l) => l.callback());
  }

  exportSVG() {
    if (typeof XMLSerializer === 'undefined')
      throw new Error('Cannot export unless XMLSerializer is available in the global context.');
    const s = new window.XMLSerializer();
    return s.serializeToString(this.ctx.svg);
  }

  downloadPNG(fileName: string) {
    if (typeof window === 'undefined')
      throw new Error('PNG export is only available in the browser environment.');
    const data = this.exportSVG();
    const canvas = document.createElement('canvas');
    canvas.style.width = `${this.width * 5}px`;
    canvas.style.height = `${this.height * 5}px`;
    canvas.width = this.width * 5;
    canvas.height = this.height * 5;
    function triggerDownload(imgURI: string) {
      const evt = new window.MouseEvent('click', {
        view: window,
        bubbles: false,
        cancelable: true,
      });

      const a = document.createElement('a');
      a.setAttribute('download', `${fileName || 'utheory-sandbox'}.png`);
      a.setAttribute('href', imgURI);
      a.setAttribute('target', '_blank');

      a.dispatchEvent(evt);
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw Error('Cannot download image without a canvas context');
    }
    const DOMURL = window.URL || window.webkitURL;

    const img = new window.Image();
    const svgBlob = new window.Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const url = DOMURL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      DOMURL.revokeObjectURL(url);

      const imgURI = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');

      triggerDownload(imgURI);
    };

    img.src = url;
  }

  /**
   * Create bounding boxes for all of the Stave & GhostNotes in a measure
   * @param tickables
   */
  setMeasureObjectsBounds(tickables: Tickable[]) {
    const editor = this.editor;
    if (!editor) {
      throw Error(`Cannot set bounding boxes without renderer.editor`);
    }

    // notes is all of the staveNotes in a given measure.

    const measureObjects: MeasureObject[] = [];

    // Find the stave shared by the notes:
    const vexStave = tickables[0]?.vexObject.getStave();

    if (!vexStave) {
      throw Error('Cannot set measure objects bounds without a vexStave');
    }

    // Determine some defaults:
    const staveStartX = vexStave.getNoteStartX();
    const staveEndX = vexStave.getNoteEndX();
    const top = vexStave.getYForLine(0 - editor.maxLegerLines);
    const bottom = vexStave.getYForLine(4 + editor.maxLegerLines);

    // Create the left boundary 'barline' object:
    measureObjects[0] = {
      isGhostOrPendingNote: false,
      barline: true,
      editable: false,
      left: staveStartX,
      right: staveStartX,
      boxLeft: staveStartX,
      boxRight: staveStartX,
      top,
      bottom,
      tickable: false,
    };

    // Create an object for each note -- calculating bounding
    // boxes for staveNotes that aren't ghosting, determining
    // any ghostNote or ghosting notes later.

    tickables.forEach((tickable) => {
      const editable = tickable.editable;

      const isEmpty = tickable.noteHolder.rendersAs === 'ghostNote';
      const isPending = tickable.noteHolder.rendersAs === 'note' && tickable.noteHolder.isEmpty();
      const isEditable = !!tickable.editable;

      // If there are two empty or uncommitted notes in a row, only the first object
      // is, in effect, editable.  Once it's been set, the second
      // can then be used.
      const priorWasUncommittedEditable =
        measureObjects[measureObjects.length - 1]?.isUncommittedEditable;

      let thisTop = top;
      let thisBottom = bottom;

      // We won't draw it, but we will setup its editable (?)
      if (isEditable && priorWasUncommittedEditable && editable instanceof Editable) {
        editor.addAccessibleEditable(editable);
      }

      // If it's empty and not pending, set its y's, but not x's until later.
      // But only add it if the prior has been committed
      if ((isEmpty || isPending) && !priorWasUncommittedEditable) {
        if (tickable.vexObject instanceof Vex.Flow.StaveNote) {
          const box = tickable.vexObject.getBoundingBox();
          thisTop = box.y < top ? box.y : top;
          thisBottom = box.y + box.h > bottom ? box.y + box.h : bottom;
        }

        measureObjects.push({
          barline: false,
          isGhostOrPendingNote: true,
          editable: editable || false,
          isUncommittedEditable: isEditable && (isEmpty || isPending),
          top: thisTop,
          bottom: thisBottom,
          tickable,
        });
        return;
      }

      // If it's a note, pending or otherwise, set its full bounding box.
      if (!isEmpty) {
        const box = tickable.vexObject.getBoundingBox();
        if (!box) {
          throw Error(`Unable to get box from vexObject`);
        }
        const left = box.x;
        const right = box.x + box.w;
        thisTop = box.y < top ? box.y : top;
        thisBottom = box.y + box.h > bottom ? box.y + box.h : bottom;
        const boxLeft = left - 0.5 * box.w;
        const boxRight = right + 0.5 * box.w;
        measureObjects.push({
          isUncommittedEditable: isEditable && (isEmpty || isPending),
          left,
          right,
          boxLeft,
          boxRight,
          top: thisTop,
          bottom: thisBottom,
          barline: false,
          isGhostOrPendingNote: false,
          editable,
          tickable,
        });
      }
    });

    // Add the final barline object.
    measureObjects.push({
      barline: true,
      isGhostOrPendingNote: false,
      left: staveEndX,
      right: staveEndX,
      boxLeft: staveEndX,
      boxRight: staveEndX,
      top,
      bottom,
      tickable: false,
    });

    // Calculate the bounding x's for ghosting notes.

    const lastNonPendingNoteInMeasure = measureObjects
      .slice(0)
      .reverse()
      .find((o) => o.tickable && o.editable);
    const lastNonPendingNoteInMeasureIndex = lastNonPendingNoteInMeasure
      ? measureObjects.indexOf(lastNonPendingNoteInMeasure)
      : -1;

    measureObjects.forEach((measureObject, measObjIndex) => {
      const isLastNoteInMeasure = measObjIndex === lastNonPendingNoteInMeasureIndex;

      if (measureObject.isGhostOrPendingNote || isLastNoteInMeasure) {
        // There's always a previous one, because there's an opening barline.
        const priorObject = measureObjects[measObjIndex - 1];
        // There's always a following one because there's a closing barline.
        const nextObject = measureObjects[measObjIndex + 1];

        // But, let's be sure about it:
        if (priorObject && nextObject) {
          // If the prior note is a ghost note, then it has
          // undefined or NaN'd boundaries. We should grab
          // left or right until we come to a valid border,
          // if we need it.
          //
          // In short: ghostNotes that aren't editable are
          // passed over -- their physical space is subsumed
          // by the note to the left, or right, that needs it.
          let leftBoundary = priorObject.boxRight;
          const needLeftBoundary =
            Number.isNaN(leftBoundary) && measureObject.tickable && measureObject.editable;
          if (needLeftBoundary) {
            const goodBoundaryObject = measureObjects
              .slice(0, measObjIndex)
              .reverse()
              .find((goodBObj) => goodBObj.boxRight && typeof goodBObj.boxRight === 'number');
            if (goodBoundaryObject) leftBoundary = goodBoundaryObject.boxRight;
          }

          // This will consume until it comes to a valid right boundary, which may be the end of the measure
          let rightBoundary = nextObject.boxLeft;
          const needRightBoundary =
            Number.isNaN(rightBoundary) && measureObject.tickable && measureObject.editable;
          if (needRightBoundary) {
            const goodBoundaryObject = measureObjects
              .slice(measObjIndex)
              .find(
                (goodBObj) =>
                  !Number.isNaN(goodBObj.boxLeft) && typeof goodBObj.boxLeft !== 'undefined',
              );
            if (goodBoundaryObject) rightBoundary = goodBoundaryObject.boxLeft;
          }

          measureObject.left = leftBoundary;
          measureObject.boxLeft = leftBoundary;
          measureObject.right = rightBoundary;
          measureObject.boxRight = rightBoundary;
        }
      }

      if (measureObject.tickable !== false && measureObject.editable) {
        const { boxLeft, top, boxRight, bottom } = measureObject;
        if (!isNumber(boxLeft) || !isNumber(top) || !isNumber(boxRight) || !isNumber(bottom)) {
          throw Error(
            `boxLeft, top, boxRight, bottom must all be numbers to set bounding box: ${{
              boxLeft,
              top,
              boxRight,
              bottom,
            }}`,
          );
        }
        measureObject.editable.setBoundingBox({
          x: boxLeft,
          y: top,
          w: boxRight - boxLeft,
          h: bottom - top,
        });
        editor.addEditable(measureObject.editable);
        editor.addAccessibleEditable(measureObject.editable);
        measureObject.editable.setClef(measureObject.tickable.clef);
        const vexStave = measureObject.tickable.vexObject.getStave();
        if (!vexStave) {
          throw Error(`Cannot set vexStave for editable, vexStave undefined.`);
        }
        measureObject.editable.setVexStave(vexStave);
        // note.note.editable.setVexObject(note.note);
        measureObject.editable.vexObject = measureObject.tickable.vexObject;
      }
    });

    this.log(
      measureObjects.map((mo, i) => `MeasureObject ${i}: boxright = ${mo.boxRight}`).join('\n'),
    );

    return measureObjects;
  }

  addCustomKeySignatureEditables() {
    const editor = this.editor;
    if (!editor) {
      throw Error(`Cannot add custom key signature editables when renderer.editor is undefined.`);
    }
    const addEditable = editor.addEditable.bind(editor);

    this.customKeySignatures.forEach((cks) => {
      cks.getEditables().forEach((editable, i, editables) => {
        addEditable(editable);
        editable.setRenderer(this);
        editable.setEditor(editor);
        // if it's the last one, widen the bounding box to the staff edge:
        if (i === editables.length - 1) {
          const stave = editable.stave;
          if (!stave) {
            throw Error(
              `Cannot set custom key sig editable without already setting the editable stave.`,
            );
          }
          const staveEnd = stave.getNoteEndX();
          const box = editable.getBoundingBox();
          box.w = staveEnd - box.x - 1;
          editable.setBoundingBox(box);
        }
      });
    });
  }

  setBoundingBoxes() {
    if (!this.editor) {
      throw Error(`Cannot set bounding boxes without renderer.editor`);
    }
    // this.tickableDictionary contains all of the vex StaveNote() objects for each
    // measure and voice:
    // this.tickableDictionary[measureNumber][voiceNumber]

    this.editor.clearEditables();

    this.addCustomKeySignatureEditables();
    for (let measure = 0; measure < this.totalMeasures; measure++) {
      for (let voice = 0; voice < this.music.voices.length; voice++) {
        const voiceTickables = this.tickableDictionary[measure]?.[voice];
        if (typeof voiceTickables !== 'undefined') {
          this.setMeasureObjectsBounds(voiceTickables);
        }
      }
    }

    this.editor.renderTextInputs();
  }
}

function isStaveNote(staveNote: any): staveNote is StaveNote {
  return staveNote instanceof StaveNote;
}
